import React, { PropsWithChildren, useMemo } from 'react';
import { SliderContentProps } from '../props';
import styles from './styles.module.scss';
import SliderStepper from './Stepper';
import { useIsMobile } from '@starsoft/common/hooks';

/**
 * SliderContent Component
 * A content wrapper component for slider/carousel that handles item layout, touch/mouse interactions,
 * responsive behavior, and navigation controls.
 *
 * @component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Slider content/items to be rendered
 * @param {boolean} props.showButtonsOnScroll - Whether to show navigation buttons only when content is scrollable
 * @param {number} props.size - Number of items to show per view on desktop
 * @param {Object} props.containerProps - Props for container behavior and state
 * @param {boolean} props.hideButtons - Whether to hide navigation buttons
 * @param {boolean} props.absoluteButton - Whether buttons should have absolute positioning
 * @param {number} props.mobileSize - Number of items to show per view on mobile
 * @returns {JSX.Element} The rendered slider content component
 *
 * @example
 * <SliderContent
 *   size={4}
 *   mobileSize={2}
 *   showButtonsOnScroll={true}
 *   hideButtons={false}
 *   absoluteButton={true}
 *   containerProps={{
 *     containerRef: ref,
 *     handleMouseDown: (e) => {},
 *     handleMouseMove: (e) => {},
 *     handleMouseUp: () => {},
 *     handleMouseLeave: () => {},
 *     currentStep: 0,
 *     onStepClick: (step) => {},
 *     hasScroll: true,
 *     isDragging: false,
 *     steps: 3
 *   }}
 * >
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 *   <div>Slide 3</div>
 * </SliderContent>
 */
export default function SliderContent({
  children,
  showButtonsOnScroll,
  size,
  containerProps,
  hideButtons,
  absoluteButton,
  mobileSize,
}: PropsWithChildren<SliderContentProps>) {
  const {
    containerRef,
    handleMouseDown,
    handleMouseLeave,
    handleMouseMove,
    currentStep,
    onStepClick,
    handleMouseUp,
    hasScroll,
    isDragging,
    steps,
  } = containerProps ?? {};

  // Check if current viewport is mobile
  const isMobile: boolean = useIsMobile();

  // Calculate number of items to show based on viewport
  const itemsPerView: number = isMobile && mobileSize ? mobileSize : size;

  // Determine if navigation buttons should be visible
  const isScrollable: boolean =
    !showButtonsOnScroll || (showButtonsOnScroll && hasScroll);

  // Memoized sections - either raw children or wrapped in container divs
  const Sections = useMemo(
    () =>
      (isMobile && !mobileSize) || !size
        ? children
        : React.Children.toArray(children).map((child, idx) => (
            <div className={styles.container__item} key={`slider-item-${idx}`}>
              {child}
            </div>
          )),
    [isMobile, children, size, mobileSize],
  );

  return (
    <div
      className={styles.container}
      style={
        {
          '--items-per-view': itemsPerView,
        } as React.CSSProperties
      }
    >
      <div
        className={`${styles.container__content} ${isDragging ? styles['container__content--dragging'] : ''}`}
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        dir="ltr"
      >
        {Sections ?? children}
      </div>
      {!hideButtons && isScrollable && steps > 1 && (
        <SliderStepper
          currentStep={currentStep as number}
          onStepClick={onStepClick!}
          steps={steps}
          absolute={absoluteButton}
        />
      )}
    </div>
  );
}
